import React, { useMemo, useState } from 'react';
import { Download, Loader2, Upload } from 'lucide-react';
import { bulkCreateUsers, downloadUserTemplate } from './api';
import type { BulkRowResult, UsersMeta } from './types';
import { helperClass, primaryBtnClass, secondaryBtnClass } from '../styles';

interface BulkCreateUserProps {
  meta: UsersMeta;
}

const BulkCreateUser: React.FC<BulkCreateUserProps> = ({ meta }) => {
  const [fileName, setFileName] = useState<string | null>(null);
  const [csv, setCsv] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<BulkRowResult[] | null>(null);
  const [summary, setSummary] = useState<string | null>(null);
  const [previewed, setPreviewed] = useState(false);

  const typeOptions = meta.user_types.map((t) => `${t.name} (${t.key})`).join(', ');
  const catalyxOptions = meta.catalyx_roles.map((r) => `${r.name} (${r.key})`).join(', ');
  const clientOptions = meta.client_roles.map((r) => `${r.name} (${r.key})`).join(', ');

  const errorCount = useMemo(
    () => (results || []).filter((r) => r.status === 'error').length,
    [results]
  );

  const onFile = async (file: File | null) => {
    setResults(null);
    setSummary(null);
    setPreviewed(false);
    setError(null);
    if (!file) {
      setFileName(null);
      setCsv(null);
      return;
    }
    const text = await file.text();
    setFileName(file.name);
    setCsv(text);
  };

  const run = async (dryRun: boolean) => {
    if (!csv) {
      setError('Choose a CSV file first.');
      return;
    }
    setBusy(true);
    setError(null);
    setSummary(null);
    try {
      const payload = await bulkCreateUsers(csv, dryRun);
      setResults(payload.results);
      setPreviewed(true);
      if (dryRun) {
        setSummary(
          payload.errors
            ? `Preview finished with ${payload.created} ready and ${payload.errors} with problems.`
            : `Preview looks good: ${payload.created} user${payload.created === 1 ? '' : 's'} ready to create.`
        );
      } else {
        setSummary(
          `Imported ${payload.created} user${payload.created === 1 ? '' : 's'}` +
            (payload.errors ? `, ${payload.errors} skipped with errors.` : '.')
        );
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Bulk create failed');
    } finally {
      setBusy(false);
    }
  };

  const handleDownload = async () => {
    setError(null);
    try {
      await downloadUserTemplate();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to download template');
    }
  };

  return (
    <div className="max-w-4xl">
      <p className="text-sm text-[#626262] mb-4">
        Import several accounts from a CSV file. Download the template first — it includes the
        column names, one example row, and the allowed values for your account.
      </p>
      <div className="rounded-lg border border-[#E8E8E8] bg-[#FAFAFA] px-4 py-3 text-[13px] text-[#4A4A4A] mb-4 space-y-1">
        <p>
          <span className="font-medium">Required:</span> email, organization (name) or
          organization_id, user type, and the matching role.
        </p>
        <p>
          <span className="font-medium">Optional:</span> first name, last name, company.
        </p>
        <p>
          <span className="font-medium">User type:</span> {typeOptions || '—'}
        </p>
        <p>
          <span className="font-medium">Catalyx roles:</span> {catalyxOptions || '—'}
        </p>
        <p>
          <span className="font-medium">Client roles:</span> {clientOptions || '—'}
        </p>
        <p className={helperClass}>
          Catalyx users need a Catalyx role (leave Client role blank). Client users need a Client
          role (leave Catalyx role blank). You can only import into organizations you can access.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <button type="button" onClick={() => void handleDownload()} className={secondaryBtnClass}>
          <Download className="w-4 h-4 inline mr-1.5" />
          Download template
        </button>
        <label className={`${secondaryBtnClass} cursor-pointer inline-flex items-center`}>
          <Upload className="w-4 h-4 inline mr-1.5" />
          {fileName || 'Choose CSV'}
          <input
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={(e) => void onFile(e.target.files?.[0] || null)}
          />
        </label>
        <button
          type="button"
          disabled={busy || !csv}
          onClick={() => void run(true)}
          className={secondaryBtnClass}
        >
          {busy && <Loader2 className="w-4 h-4 animate-spin inline mr-1.5" />}
          Preview
        </button>
        <button
          type="button"
          disabled={busy || !csv || !previewed}
          onClick={() => void run(false)}
          className={primaryBtnClass}
        >
          Import
        </button>
      </div>
      {!previewed && csv && (
        <p className="text-[12px] text-[#8A8A8A] mb-3">Preview the file before importing.</p>
      )}

      {error && (
        <div className="mb-4 rounded-md bg-red-50 text-red-700 px-3 py-2 text-sm">{error}</div>
      )}
      {summary && (
        <div
          className={`mb-4 rounded-md px-3 py-2 text-sm ${
            errorCount ? 'bg-amber-50 text-amber-900' : 'bg-emerald-50 text-emerald-800'
          }`}
        >
          {summary}
        </div>
      )}

      {results && (
        <div className="overflow-x-auto border border-[#E8E8E8] rounded-lg">
          <table className="w-full text-sm">
            <thead className="bg-[#FAFAFA] text-left text-[12px] text-[#626262]">
              <tr>
                <th className="px-3 py-2.5 font-semibold">Row</th>
                <th className="px-3 py-2.5 font-semibold">Email</th>
                <th className="px-3 py-2.5 font-semibold">Status</th>
                <th className="px-3 py-2.5 font-semibold">Message</th>
              </tr>
            </thead>
            <tbody>
              {results.map((row) => (
                <tr key={`${row.row}-${row.email}`} className="border-t border-[#E8E8E8]">
                  <td className="px-3 py-2">{row.row ?? '—'}</td>
                  <td className="px-3 py-2">{row.email || '—'}</td>
                  <td className="px-3 py-2 capitalize">{row.status}</td>
                  <td
                    className={`px-3 py-2 ${
                      row.status === 'error' ? 'text-red-700' : 'text-[#4A4A4A]'
                    }`}
                  >
                    {row.message}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default BulkCreateUser;
